import type {
  DialogPos,
  DialogSettings,
  OptionalDialogSettings,
  PositionDetails,
} from './ContextDialog.entities';

const DIALOG_OFFSET = 8;
const DIALOG_MAX_WIDTH = 288;
const DEFAULT_POS = 'bottom';

const getMergedSettings = (settings: OptionalDialogSettings): DialogSettings => {
  return {
    offset: settings.offset ?? DIALOG_OFFSET,
    maxWidth: settings.maxWidth ?? DIALOG_MAX_WIDTH,
    defaultPos: settings.defaultPos ?? DEFAULT_POS,
  };
};

const getPosParts = (position: DialogPos) => {
  const parts = position.split('-');
  return { targetSide: parts[0], contentFlow: parts[1] };
};

const calcCoordsForPos = (
  element: HTMLButtonElement,
  position: DialogPos,
  settings: DialogSettings,
): PositionDetails => {
  const dialogOffset = settings.offset;
  const targetBounds = element.getBoundingClientRect();
  const { x, y, width: w, height: h } = targetBounds;
  const { targetSide, contentFlow } = getPosParts(position);
  const pageX = x;
  const pageY = y;
  let dialogX = pageX;
  let dialogY = pageY;

  if (targetSide === 'top' || targetSide === 'bottom') {
    dialogY = targetSide === 'top' ? pageY - dialogOffset : pageY + h + dialogOffset;

    if (!contentFlow) {
      dialogX = pageX + w / 2;
    } else if (contentFlow === 'right') {
      dialogX = pageX;
    } else if (contentFlow === 'left') {
      dialogX = pageX + w;
    }
  }

  if (targetSide === 'left' || targetSide === 'right') {
    dialogX = targetSide === 'left' ? pageX - dialogOffset : pageX + w + dialogOffset;

    if (!contentFlow) {
      dialogY = pageY + h / 2;
    } else if (contentFlow === 'top') {
      dialogY = pageY + h;
    } else if (contentFlow === 'bottom') {
      dialogY = pageY;
    }
  }

  return { coords: [dialogX, dialogY], name: position };
};

//NOTE: at the moment we can only check left and right bounds
//Because we know that max width of the dialog, but as there is no max height
//and is doesn't exist in the dom at this point, we cant know the height
const willDialogFitInVP = (posDetails: PositionDetails, settings: DialogSettings) => {
  const { targetSide, contentFlow } = getPosParts(posDetails.name);
  const [x] = posDetails.coords;
  const dialogMaxWidth = settings.maxWidth;
  const dialogHalfWidth = dialogMaxWidth / 2;
  const isLeftFacing = targetSide === 'left' || contentFlow === 'left';
  const isRightFacing = targetSide === 'right' || contentFlow === 'right';

  const leftDiff = (x - dialogMaxWidth) * -1;
  const rightDiff = x + dialogMaxWidth - window.innerWidth;
  const centreLeftDiff = (x - dialogHalfWidth) * -1;
  const centreRightDiff = x + dialogHalfWidth - window.innerWidth;

  if (isLeftFacing && leftDiff > 0) {
    return {
      willFit: false,
      diff: leftDiff,
    };
  }

  if (isRightFacing && rightDiff > 0) {
    return {
      willFit: false,
      diff: rightDiff,
    };
  }

  if (
    !contentFlow &&
    (targetSide === 'top' || targetSide === 'bottom') &&
    (centreLeftDiff > 0 || centreRightDiff > 0)
  ) {
    return {
      willFit: false,
      diff: Math.max(centreLeftDiff, centreRightDiff),
    };
  }

  return {
    willFit: true,
    diff: 0,
  };
};

//if the requested position wont fit in the viewport, try the next position along horizontally
const roundRobinPos = (position: DialogPos, settings: DialogSettings): DialogPos => {
  if (!position) return settings.defaultPos;
  const { targetSide, contentFlow } = getPosParts(position);

  if (targetSide === 'top' || targetSide === 'bottom') {
    if (contentFlow === 'left') {
      return targetSide;
    }
    if (!contentFlow) {
      return `${targetSide}-right`;
    }
    if (contentFlow === 'right') {
      return `${targetSide}-left`;
    }
  }

  if (targetSide === 'right') {
    if (!contentFlow) return 'left';
    return `left-${contentFlow}` as DialogPos;
  }

  if (targetSide === 'left') {
    if (contentFlow === 'top') return 'top';
    return 'bottom';
  }

  return settings.defaultPos;
};

const getDialogCoords = (
  element: HTMLButtonElement,
  requestedPosition: DialogPos,
  settings: OptionalDialogSettings,
) => {
  const mergedSettings = getMergedSettings(settings);
  let posDetails = calcCoordsForPos(element, requestedPosition, mergedSettings);
  let attmpt = willDialogFitInVP(posDetails, mergedSettings);
  let testPosition = roundRobinPos(requestedPosition, mergedSettings);
  const outsideBoundsScores = [{ ...attmpt, posDetails: { ...posDetails } }];

  //we test it 2 times, so that all the possible round robin positions of a given position are tested
  while (!attmpt.willFit && outsideBoundsScores.length < 3) {
    const testPosDetails = calcCoordsForPos(element, testPosition, mergedSettings);
    attmpt = willDialogFitInVP(testPosDetails, mergedSettings);

    if (attmpt.willFit) {
      posDetails = testPosDetails;
    } else {
      testPosition = roundRobinPos(testPosition, mergedSettings);
      outsideBoundsScores.push({ ...attmpt, posDetails: { ...testPosDetails } });
    }
  }

  if (attmpt.willFit) {
    return posDetails;
  } else {
    //if all positions fall outside VP bounds find the object with the smallest diff
    const closest = outsideBoundsScores.reduce((prev, current) =>
      prev.diff < current.diff ? prev : current,
    );
    return closest.posDetails;
  }
};

export { calcCoordsForPos, getDialogCoords, getPosParts, roundRobinPos, willDialogFitInVP };
